import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./baseStyles.css"; // ✅ shared layout
import "./CustomerManagerPage.css"; // ✅ reserved for overrides
const apiUrl = process.env.REACT_APP_API_URL;

const CustomerManagerPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    loginUserId: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    partnerLoginId: "",
    suppressEmail: false,
  });

  const [idToken, setIdToken] = useState(null);
  const [cognitoUserId, setCognitoUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customerData, setCustomerData] = useState(null);
  const [infoMessage, setInfoMessage] = useState("");
  
const VAPID_PUBLIC_KEY = "BCp91QC_30Eb8-agcm-iCjECIPZUv9QKqS2jIP08UhpBVbI5HRMOXy7UPwuNWl7fr4mIEAoNapn4t-EB-JVWaM8";

  const [subscriptionStatus, setSubscriptionStatus] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("idToken");
    if (!token) return navigate("/login");
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) throw new Error("Expired token");
      setIdToken(token);
      setCognitoUserId(decoded.sub);
      fetchCustomerData(decoded.sub);
    } catch {
      navigate("/login");
    }
  }, []);


  const clearStatusMessages = () => {
    setError("");
    setInfoMessage(""); 
  };


  const onPhoneBlur = () => {
    const { valid, display } = normalizeUSPhone(form.phone);
    if (valid) setForm(prev => ({ ...prev, phone: display }));
  };

  const fetchCustomerData = async (userid) => {
    try {
      setLoading(true);
      clearStatusMessages();
      const res = await axios.get(`${apiUrl}`, {
        params: { operation: "fetch_customer", userid },
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      });
      const data = res.data?.customer || {};
      setCustomerData(data);
      setForm({
        loginUserId: data.loginUserId || "",
        email: data.email || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        phone: formatUSForDisplay(data.phone || ""),
        partnerLoginId: data.partnerId || "",
        suppressEmail: data.suppressEmail || false,
        pollingNotificationsForFavorites: data.pollingNotificationsForFavorites || false,
        wantsTextNotification: data.wantsTextNotification || false,

      });
    } catch {
      setError("Failed to load customer data.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };







const handleUpdateCustomer = async () => {
  if (!cognitoUserId) return setError("Not authenticated.");
  clearStatusMessages();

  // 1) Validate phone (and require it if texting is enabled)
  const phoneInput = (form.phone || "").trim();
  let phoneE164 = null;

  if (phoneInput) {
    const { valid, e164, error } = normalizeUSPhone(phoneInput);
    if (!valid) {
      setError(error || "Invalid phone number format.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    phoneE164 = e164; // normalized +1XXXXXXXXXX
  } else if (form.wantsTextNotification) {
    setError("Enter a valid US mobile number to enable Text Messages.");
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  try {
    setLoading(true);

    // 2) Build payload
    const payload = {
      operation: "update_customer",
      userid: cognitoUserId,
      loginUserId: form.loginUserId,
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      partnerLoginId: form.partnerLoginId || "",
      suppressEmail: form.suppressEmail,
      pollingNotificationsForFavorites: form.pollingNotificationsForFavorites,
      wantsTextNotification: form.wantsTextNotification,
    };

    // Decide how to send phone:
    // - empty input => clear it server-side
    // - valid input => save E.164
    if (phoneInput === "") {
      payload.phone = "";
      // Optional: also disable texting when phone is cleared
      // payload.wantsTextNotification = false;
    } else if (phoneE164) {
      payload.phone = phoneE164;
    }
    // else: omit to keep existing server value

    // 3) Save
    await axios.post(`${apiUrl}`, payload, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    // 4) Reflect saved value in UI
    setForm(prev => ({
      ...prev,
      phone: phoneE164 ? formatUSForDisplay(phoneE164) : "",
      // If you chose to force-disable texting when phone cleared, mirror it here too:
      // wantsTextNotification: phoneE164 ? prev.wantsTextNotification : false,
    }));

    setInfoMessage("Customer updated successfully!");
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (err) {
    setError(err?.response?.data?.error || "Failed to update customer.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  } finally {
    setLoading(false);
  }
};










return (
  <div className="container customer-form">
    <h2 className="page-header">Profile Information</h2>

    {error && <div className="error-message">{error}</div>}
    {infoMessage && <div className="success-message">{infoMessage}</div>}
    {(error || infoMessage) && <div style={{ marginBottom: "16px" }} />}


    {/* ✅ Desktop layout */}
    <div className="desktop-only">
      <div className="form-table">
        <div className="form-row">
          <label htmlFor="loginUserId">Login ID</label>
          <input
            id="loginUserId"
            name="loginUserId"
            value={form.loginUserId}
            onChange={handleChange}
            placeholder="User ID used to sign in"
          />
        </div>

        <div className="form-row">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email address"
          />
        </div>

        <div className="form-row">
          <label htmlFor="firstName">First Name</label>
          <input
            id="firstName"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            placeholder="First name"
          />
        </div>

        <div className="form-row">
          <label htmlFor="lastName">Last Name</label>
          <input
            id="lastName"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            placeholder="Last name"
          />
        </div>

        <div className="form-row">
          <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              onBlur={onPhoneBlur}
              placeholder="Phone number"
            />

        </div>


  {/* Do not email me */}
  <div className="checkbox-row">
    <input
      type="checkbox"
      id="suppressEmail"
      name="suppressEmail"
      checked={form.suppressEmail || false}
      onChange={handleChange}
    />
    <label htmlFor="suppressEmail" className="checkbox-title">
      Do not email me
    </label>
    {/* no note needed here */}
  </div>

  {/* Text messages */}
  <div className="checkbox-row">
    <input
      type="checkbox"
      id="wantsTextNotification"
      name="wantsTextNotification"
      checked={form.wantsTextNotification || false}
      onChange={handleChange}
    />
    <label htmlFor="wantsTextNotification" className="checkbox-title">
      Text Messages
    </label>
    {/* NOTE spans under the checkbox (bleed) */}
    <label htmlFor="wantsTextNotification" className="checkbox-note">
      <span className="note-legal">
        I agree to receive text reminders from MyDailyReminders. Typically one message per day,
        with additional messages upon request. Msg &amp; data rates may apply. Reply STOP to opt
        out, HELP for help. <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> |
        <a href="/privacy" target="_blank" rel="noopener noreferrer"> Privacy</a>
      </span>
      <br></br>
      <span className="note-strong">
        <span className="label">PLEASE NOTE:</span> Texts will come from
        <span className="number"> 1-660-728-7295</span>. For best results, save this
        number as “myDailyReminders” in your contacts.
      </span>

    </label>
  </div>



    <div className="button-group">
      <button className="save-button" onClick={handleUpdateCustomer} disabled={loading}>
        Update Profile
      </button>
    </div>


      </div>
    </div>





    {/* ✅ Mobile layout */}
    <div className="mobile-only edit-options-container">
      <div className="form-field">
        <label htmlFor="loginUserId">Login ID</label>
        <input
          id="loginUserId"
          name="loginUserId"
          value={form.loginUserId}
          onChange={handleChange}
          placeholder="User ID used to sign in"
        />
      </div>

      <div className="form-field">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="Email address"
        />
      </div>

      <div className="form-field">
        <label htmlFor="firstName">First Name</label>
        <input
          id="firstName"
          name="firstName"
          value={form.firstName}
          onChange={handleChange}
          placeholder="First name"
        />
      </div>

      <div className="form-field">
        <label htmlFor="lastName">Last Name</label>
        <input
          id="lastName"
          name="lastName"
          value={form.lastName}
          onChange={handleChange}
          placeholder="Last name"
        />
      </div>

      <div className="form-field">
        <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            onBlur={onPhoneBlur}
            placeholder="Phone number"
          />

      </div>





  {/* Do not email me */}
  <div className="checkbox-row">
    <input
      type="checkbox"
      id="suppressEmail"
      name="suppressEmail"
      checked={form.suppressEmail || false}
      onChange={handleChange}
    />
    <label htmlFor="suppressEmail" className="checkbox-title">
      Do not email me
    </label>
    {/* no note needed here */}
  </div>

  {/* Text messages */}
  <div className="checkbox-row">
    <input
      type="checkbox"
      id="wantsTextNotification"
      name="wantsTextNotification"
      checked={form.wantsTextNotification || false}
      onChange={handleChange}
    />
    <label htmlFor="wantsTextNotification" className="checkbox-title">
      Text Messages
    </label>
    {/* NOTE spans under the checkbox (bleed) */}
    <label htmlFor="wantsTextNotification" className="checkbox-note">
      <span className="note-legal">
        I agree to receive text reminders from MyDailyReminders. Typically one message per day,
        with additional messages upon request. Msg &amp; data rates may apply. Reply STOP to opt
        out, HELP for help. <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> |
        <a href="/privacy" target="_blank" rel="noopener noreferrer"> Privacy</a>
      </span>
      <br></br>
      <span className="note-strong">
        <span className="label">PLEASE NOTE:</span> Texts will come from
        <span className="number"> 1-660-728-7295</span>. For best results, save this
        number as “myDailyReminders” in your contacts.
      </span>

    </label>

  </div>







    <div className="button-group">
      <button className="save-button" onClick={handleUpdateCustomer} disabled={loading}>
        Update Profile
      </button>
    </div>





  </div>


  </div>
);



};

export default CustomerManagerPage;


// Normalize & validate a US phone number for SMS (AWS SNS needs E.164: +1XXXXXXXXXX)
function normalizeUSPhone(input) {
  if (!input) return { valid: false, error: "Enter a phone number." };

  const withoutExt = String(input).replace(/(?:ext\.?|x|#)\s*\d+$/i, "");
  let digits = withoutExt.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return { valid: false, error: "US numbers must have 10 digits." };

  const area = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  const line = digits.slice(6);

  if (/[01]/.test(area[0]) || /[01]/.test(exchange[0]))
    return { valid: false, error: "Area/exchange cannot start with 0 or 1." };

  if (exchange[1] === "1" && exchange[2] === "1")
    return { valid: false, error: "N11 exchanges are not valid." };

  if (/^(\d)\1{9}$/.test(digits)) return { valid: false, error: "Number looks invalid (repeated digits)." };
  if (area === "000" || exchange === "000" || line === "0000")
    return { valid: false, error: "Number contains an all-zero block." };
  if (exchange === "555" && Number(line) >= 100 && Number(line) <= 199)
    return { valid: false, error: "555-01xx range is not valid." };

  const e164 = `+1${digits}`;
  const display = `${area}-${exchange}-${line}`;
  return { valid: true, e164, display, raw10: digits };
}

// Pretty-print E.164 back to AAA-PPP-NNNN for UI
function formatUSForDisplay(value) {
  if (!value) return "";
  const m = String(value).match(/^\+?1?(\d{10})$/);
  if (!m) return value;
  const d = m[1];
  return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
}
